// src/shared/components/ui/RangeDatePicker.tsx
"use client"

import * as React from "react"
import { format, addDays, startOfDay, endOfDay, startOfWeek, startOfMonth } from "date-fns"
import { Calendar, RangeCalendar } from "./calendar"
import { Popover, PopoverTrigger, PopoverContent } from "./popover"
import { Button } from "./button"
import type { DateRange } from 'react-day-picker'

export interface RangeStrings {
  from: string  // "YYYY-MM-DDTHH:mm:ss"
  to:   string
}

export interface RangeDatePickerProps {
  initialRange?: RangeStrings
  onRangeChange: (r: RangeStrings | undefined) => void
}

export function RangeDatePicker({
  initialRange,
  onRangeChange
}: RangeDatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [draft, setDraft] = React.useState<RangeStrings | undefined>(initialRange)

  // Preset ranges
  const now = new Date()
  const presets: { label: string; range: RangeStrings }[] = [
    {
      label: "Today",
      range: {
        from: format(startOfDay(now), "yyyy-MM-dd'T'HH:mm:ss"),
        to:   format(endOfDay(now),   "yyyy-MM-dd'T'HH:mm:ss")
      }
    },
    {
      label: "Last 7 Days",
      range: {
        from: format(startOfDay(addDays(now, -6)), "yyyy-MM-dd'T'HH:mm:ss"),
        to:   format(endOfDay(now),                 "yyyy-MM-dd'T'HH:mm:ss")
      }
    },
    {
      label: "Week to Date",
      range: {
        from: format(startOfDay(startOfWeek(now, { weekStartsOn: 1 })), "yyyy-MM-dd'T'HH:mm:ss"),
        to:   format(endOfDay(now),                             "yyyy-MM-dd'T'HH:mm:ss")
      }
    },
    {
      label: "Month to Date",
      range: {
        from: format(startOfDay(startOfMonth(now)), "yyyy-MM-dd'T'HH:mm:ss"),
        to:   format(endOfDay(now),               "yyyy-MM-dd'T'HH:mm:ss")
      }
    }
  ]

  const commit = (range?: DateRange) => {
    if (range && range.from && range.to) {
      const fromDate = new Date(range.from)
      fromDate.setHours(0,0,0,0)
      const toDate = new Date(range.to)
      toDate.setHours(23,59,59,999)
      const out: RangeStrings = {
        from: format(fromDate, "yyyy-MM-dd'T'HH:mm:ss"),
        to:   format(toDate,   "yyyy-MM-dd'T'HH:mm:ss")
      }
      setDraft(out)
      onRangeChange(out)
      setOpen(false)
    } else {
      setDraft(undefined)
    }
  }

  const applyPreset = (range: RangeStrings) => {
    setDraft(range)
    onRangeChange(range)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          {draft?.from && draft.to
            ? `${draft.from} – ${draft.to}`
            : "Select date range"}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex bg-neutral-600 p-1 rounded-md shadow-lg gap-2">
        {/* Left presets column */}
        <div className="flex flex-col items-start space-y-1 pr-4 border-r border-neutral-800">
          {presets.map(p => (
            <Button
              key={p.label}
              variant="ghost"
              size="sm"
              className="justify-start text-neutral-200 font-medium text-xs"
              onClick={() => applyPreset(p.range)}
            >
              {p.label}
            </Button>
          ))}
        </div>

        {/* Right calendar */}
        <div className="bg-neutral-800 p-2 rounded-md flex-shrink-0"></div>
        <RangeCalendar
          mode="range"
          required={false}
          defaultMonth={initialRange ? new Date(initialRange.from) : undefined}
          selected={
            draft && { from: new Date(draft.from), to: new Date(draft.to) }
          }
          onSelect={commit}
          numberOfMonths={2}
          className="bg-neutral-800 rounded-md"
        // classNames={{
        //   /* 1) Center the month header */
        //   caption:       "flex justify-center items-center px-2 py-1",
        //   caption_label: "text-neutral-400 font-medium text-base text-center",

        //   /* 2) Color all weekday headings & days */
        //   head_cell:     "w-8 py-1 text-xs font-medium text-neutral-400 text-center",
        //   day:           "w-8 h-8 flex items-center justify-center text-sm text-neutral-400 hover:bg-neutral-700 rounded",

        //   /* Today’s circle (when not selected) */
        //   day_today:     "bg-neutral-700 text-emerald-300 font-semibold rounded",

        //   day_disabled:  "text-neutral-600 opacity-50 cursor-not-allowed",
        // }}          
          classNames={{
           // month header container (you can also tweak caption here)
           caption:       "flex justify-center items-center px-2 py-1",
           // month name itself
           caption_label: "text-neutral-300 font-medium text-base",
           head_cell:     "w-8 py-1 text-xs font-medium text-neutral-400 text-center",
           
          }}          
          modifiersClassNames={{
            /* single-day or the “middle” days */
            selected:     "bg-neutral-200 text-white font-medium rounded-md",
            range_middle: "bg-neutral-500/20 text-white font-medium",

            /* endpoints */
            range_start:  "bg-neutral-700 text-white font-medium rounded-l-lg",
            range_end:    "bg-neutral-800 text-white font-medium rounded-r-lg",
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
